import type { CasinoSharedRootState } from '~/components/casino-shared/ducks/types';
import type { EnhancedGame, Game } from '~/components/casino-shared/types';
import enhanceImageView from '~/components/casino-shared/utilities/enhanceImage';
import getGameUrls from '~/components/casino-shared/utilities/getGameUrls';
import { createImageViewFactory } from '~/components/utilities/image-format';
import { requestApi } from '~/services/api-new';

type SmartSearchResponse = {
	query: string;
	result: Game[];
	recommendation: Game[];
	searchId: string;
	succeeded: boolean;
};

const graphyteSearch = async (
	query: string,
	getState: () => CasinoSharedRootState,
	imageDescriptors: string[],
	imageFormats: string[],
	license: string
) => {
	if (query.length <= 2) {
		return {
			results: [],
			pending: false,
			succeeded: true
		};
	}

	try {
		const data = await requestApi<SmartSearchResponse>({
			endpoint: `/rest/casino/smart-search`,
			method: 'POST',
			payload: {
				query,
				title: document.title,
				url: `${window.location.origin}${window.location.pathname}`
			}
		});

		const globalState = getState();

		// if search results are for an outdated search do not proceed with data manipulations and throw quickly
		if (globalState.search.keyword !== data.query) {
			throw new Error('query has changed since the last request');
		}

		const formatEnhancer = createImageViewFactory(imageFormats, imageDescriptors);

		const enhanceGamesArray = (gamesArray: Game[]) => {
			return gamesArray.map(game => {
				const enhancedGame: EnhancedGame = {
					...game,
					...getGameUrls(game, license),
					imageView: enhanceImageView(game.imageView, imageDescriptors, formatEnhancer)
				};

				return enhancedGame;
			});
		};

		const enhancedGames = enhanceGamesArray(data.result);

		const enhancedRecommendations = enhanceGamesArray(data.recommendation);

		return {
			results: enhancedGames,
			recommendation: enhancedRecommendations,
			searchId: data.searchId,
			succeeded: data.succeeded,
			pending: false
		};
	} catch {
		return {
			succeeded: false,
			pending: false
		};
	}
};

const graphyteSearchConversion = (gameCode: string, searchId: string, query: string) => {
	void requestApi({
		endpoint: '/rest/casino/smart-search/click',
		method: 'POST',
		payload: {
			query,
			searchId,
			gameCode
		}
	});
};

export { graphyteSearch, graphyteSearchConversion };
